import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { SimulationActionResponseDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { WlmDialogSettings } from '@common-modules/shared/model/dialog/wlm-dialog-setting';
import { SelectOption } from '@common-modules/shared/model/shared/select-option';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InstanceUpdatesManagerService } from '../../../../../cloud-twin/src/app/cloud-twin-shared/workers/instance-updates-manager.service';
import { MasterOperatorConnectItemsSettings } from '../models/master-operator-connect-settings';
import { MasterOperatorType } from '../models/master-operator-type';
import { instanceBelongsToConnectSettings } from '../operators/instance-belongs-to-settings.operator';
import { MasterOperatorSimulateData } from './master-operator-simulate-data';
import { TCSimulationService } from './ts-simulation.service';

const maskIncompleteValidator = (control: AbstractControl): { [key: string]: boolean } | null => {
  const value = control?.value;
  if (value && typeof value === 'string' && value.includes('_')) {
    return {
      maskIncomplete: true,
    };
  }

  return null;
};

function getInstanceIdFromSnapshotId(
  connectSettings: MasterOperatorConnectItemsSettings,
  snapshotId: string
): string {
  const item = connectSettings.items.find((item) => item.snapshotId === snapshotId);
  return item?.instanceId;
}

const COMPONENT_SELECTOR = 'tc-master-operator-simulate-form';

@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './master-operator-simulate-form.component.html',
  styleUrls: ['./master-operator-simulate-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MasterOperatorSimulateFormComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class MasterOperatorSimulateFormComponent implements OnInit, ControlValueAccessor {
  private _connectSettings: MasterOperatorConnectItemsSettings;
  get connectSettings(): MasterOperatorConnectItemsSettings {
    return this._connectSettings;
  }
  @Input() set connectSettings(value: MasterOperatorConnectItemsSettings) {
    this._connectSettings = value;
    if (this.connectSettings) {
      this.isDisabled = this.connectSettings?.type === MasterOperatorType.Master;
      this.setupSelectorOptions();
      this.buildForm();
    }
  }
  @Input() fieldClass: string;
  @Input() fieldAppearance = 'outline';

  form: FormGroup;
  isValid: boolean = true;
  isCyclic: boolean;
  isExternalDisabled: boolean;
  snapshotOptions: SelectOption<string>[];
  scenarioOptions: SelectOption<string>[] = [];
  allScenarioOptionsHash: Map<string, (SelectOption<string> & { isActive: boolean })[]>;
  isDisabled = false;
  readonly floatLabel = 'always';
  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;

  readonly dayTimeHint = 'D.HH:MM:SS';

  private nativeOnChange: (value: MasterOperatorSimulateData) => void;
  private nativeOnTouched: (value: MasterOperatorSimulateData) => void;

  private readonly _cyclicFields = ['cycle', 'period'];
  private readonly _nonCyclicFields = ['date', 'startTime', 'stopTime'];
  private readonly _defaultValidators = {
    cycle: [maskIncompleteValidator],
    period: [maskIncompleteValidator],
    startTime: [maskIncompleteValidator],
    stopTime: [maskIncompleteValidator],
  };

  constructor(
    private _fb: FormBuilder,
    private _tcSimulationService: TCSimulationService,
    private _dialogService: DialogService,
    private _instanceUpdatesService: InstanceUpdatesManagerService
  ) {}

  ngOnInit(): void {
    this.listenInstanceUpdates();
  }

  private buildForm(): void {
    const formValue = (value) => ({ value, disabled: this.isDisabled });

    this.form = this._fb.group({
      cyclic: [formValue(false), [Validators.required]],
      timeStepMax: [formValue(3600), [Validators.required]],
      timeStepMin: [formValue(720), [Validators.required]],

      // Cyclic fields
      cycle: [formValue('10:00:00'), []],
      period: [formValue('01:00:00'), []],

      // Non-cyclic fields
      date: [formValue(new Date()), []],
      startTime: [formValue('00:00:00'), []],
      stopTime: [formValue('01:00:00'), []],

      scenarioIds: [formValue([]), [Validators.required]],
      snapshotId: [formValue(null), []],
      instanceId: [formValue(null), []],
    });

    this.toggleValidators();

    this.form
      .get('cyclic')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((isCyclic) => {
        this.isCyclic = isCyclic;
        this.toggleValidators();
      });

    this.listenSnapshotChanges();

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((data) => {
      if (data) {
        const model = this.cleanModel(data);
        if (this.nativeOnChange) {
          this.nativeOnChange(model);
        }
      }
    });

    this.isValid = this.form.valid;
    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.isValid = this.form.valid;
    });

    this.setDefaultSnapshot();
  }

  private listenInstanceUpdates(): void {
    this._instanceUpdatesService.updatedInstance$
      .pipe(
        untilDestroyed(this),
        instanceBelongsToConnectSettings(() => this.connectSettings)
      )
      .subscribe((instance) => {
        this.setupSelectorOptions();
        this.setDefaultSnapshot();
      });
  }

  recursiveErrors() {
    return Object.values(this.form.controls).reduce(
      (accum, control) => accum.concat(control.errors),
      []
    );
  }

  writeValue(data: MasterOperatorSimulateData): void {
    this.form.setValue(data);
  }

  registerOnChange(fn: any): void {
    this.nativeOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.nativeOnTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isExternalDisabled = isDisabled;
  }

  onStartSimulation(): void {
    if (this.form.valid) {
      const model = this.form.value;
      this._tcSimulationService.startSimulation(model).subscribe({
        next: (response: SimulationActionResponseDto) => {
          const successKey = 'master-operator.simulation.start-success';
          this.showSimulateMessage(response, successKey);
        },
        error: (error) => {
          this._dialogService.showErrorMessage(error);
        },
      });
    }
  }

  onStopSimulation(): void {
    if (this.form.valid) {
      const model = this.form.value;
      this._tcSimulationService.stopSimulation(model.instanceId).subscribe({
        next: (response: SimulationActionResponseDto) => {
          const successKey = 'master-operator.simulation.stop-success';
          this.showSimulateMessage(response, successKey);
        },
        error: (error) => {
          this._dialogService.showErrorMessage(error);
        },
      });
    }
  }

  private listenSnapshotChanges(): void {
    this.form
      .get('snapshotId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((snapshotId) => {
        // Set is associated instance id.
        const instanceId = getInstanceIdFromSnapshotId(this.connectSettings, snapshotId);
        this.form.get('instanceId').setValue(instanceId);

        // Set associated scenario options and set default scenario.
        const scenarioOptions = this.allScenarioOptionsHash.get(snapshotId);
        this.scenarioOptions = scenarioOptions;
        const activeScenario = scenarioOptions.find((scenario) => scenario.isActive);
        this.form.get('scenarioIds').setValue([activeScenario.value]);
      });
  }

  private showSimulateMessage(response: SimulationActionResponseDto, successKey: string): void {
    if (response.message) {
      this._dialogService.showMessage(response.message, 'success');
    } else {
      this._dialogService.showTranslatedMessageInSnackBar(
        new WlmDialogSettings({
          translateKey: successKey,
          icon: 'success',
        })
      );
    }
  }

  private setDefaultSnapshot(): void {
    const defaultSnapshotId = this.connectSettings?.items?.length
      ? this.connectSettings.items[0].snapshotId
      : null;
    this.form.get('snapshotId').setValue(defaultSnapshotId);
  }

  private setupSelectorOptions(): void {
    const snapshotSet = new Set<string>();
    this.snapshotOptions = [];
    const scenariosHash = new Map<string, (SelectOption<string> & { isActive })[]>();

    this.connectSettings.items.forEach((item) => {
      // Avoid duplicating snapshots
      if (!snapshotSet.has(item.snapshotId)) {
        const snapshot = new SelectOption<string>({
          label: item.snapshotName,
          value: item.snapshotId,
        });
        this.snapshotOptions.push(snapshot);
        snapshotSet.add(item.snapshotId);
      }

      const snapshotScenarios = scenariosHash.get(item.snapshotId) ?? [];
      const scenario = {
        label: item.scenarioName,
        value: item.scenarioId,
        isActive: item.scenarioIsActive,
      };
      snapshotScenarios.push(scenario);
      scenariosHash.set(item.snapshotId, snapshotScenarios);
    });

    this.allScenarioOptionsHash = scenariosHash;
  }

  private cleanModel(initialModel: MasterOperatorSimulateData): MasterOperatorSimulateData {
    const model = globalUtilsHelper.clone(initialModel, true);
    const fieldsToDelete = model.cyclic ? this._nonCyclicFields : this._cyclicFields;

    fieldsToDelete.forEach((field) => {
      delete model[field];
    });

    return model;
  }

  private toggleValidators(): void {
    const fieldsToRequire = this.isCyclic ? this._cyclicFields : this._nonCyclicFields;
    const fieldsToRemoveRequire = this.isCyclic ? this._nonCyclicFields : this._cyclicFields;

    fieldsToRequire.forEach((field) => {
      const defaultValidators = this._defaultValidators[field] ?? [];
      this.form.get(field).setValidators([...defaultValidators, Validators.required]);
    });

    fieldsToRemoveRequire.forEach((field) => {
      const defaultValidators = this._defaultValidators[field] ?? [];
      this.form.get(field).setValidators(defaultValidators);
    });

    this.form.updateValueAndValidity();
  }
}
